import { Helmet } from "react-helmet-async";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lightbulb, PenLine } from "lucide-react";
import { redirectToGoogleLogin } from "../utils/googleAuth";
import { POST_IDEAS, getPostIdeasBySlug } from "../data/postIdeas";

export default function PostIdeasPage() {
  const { slug } = useParams();
  const topic = getPostIdeasBySlug(slug);

  if (!topic) {
    return <Navigate to="/post-ideas" replace />;
  }

  const url = `https://postwriter.tech/post-ideas/${topic.slug}`;

  return (
    <>
      <Helmet>
        <title>{`${topic.title} (2026) — PostWriter`}</title>
        <meta name="description" content={topic.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={topic.title} />
        <meta property="og:description" content={topic.metaDescription} />
        <meta property="og:image" content="https://postwriter.tech/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://postwriter.tech/og-image.png" />
        <link rel="canonical" href={url} />
      </Helmet>
      <main className="min-h-screen bg-background font-body">
        <header className="border-b border-border">
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="PostWriter" className="w-7 h-7" />
              <span className="text-lg font-bold font-headline text-foreground">PostWriter</span>
            </Link>
            <Link
              to="/post-ideas"
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> All topics
            </Link>
          </div>
        </header>

        <article className="max-w-3xl mx-auto px-6 py-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary text-muted-foreground font-headline text-[10px] font-extrabold uppercase tracking-[0.2em] mb-6">
            Post Ideas · {topic.persona}
          </span>
          <h1 className="font-headline text-4xl md:text-5xl font-extrabold tracking-tighter leading-tight text-foreground mb-6">
            {topic.title}
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed mb-12">{topic.intro}</p>

          <div className="space-y-6 mb-16">
            {topic.ideas.map((idea, i) => (
              <div
                key={i}
                className="bg-muted/40 dark:bg-card rounded-2xl p-6 ring-1 ring-border/20"
              >
                <div className="flex items-start gap-4">
                  <span className="font-headline text-2xl font-extrabold text-primary/40 shrink-0 leading-none pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <p className="font-headline text-lg font-bold text-foreground leading-snug mb-2">
                      "{idea.hook}"
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{idea.angle}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-card rounded-2xl p-8 ring-1 ring-primary/20 mb-16">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-primary" />
              <h2 className="font-headline text-xl font-bold text-foreground">
                What works for {topic.persona.toLowerCase()} on LinkedIn
              </h2>
            </div>
            <ul className="space-y-3">
              {topic.tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground leading-relaxed">
                  <span className="text-primary font-bold shrink-0 pt-px">→</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>

          <div className="text-center bg-muted/40 dark:bg-card rounded-[2rem] p-12 ring-1 ring-border/20 mb-12">
            <PenLine className="w-8 h-8 text-primary mx-auto mb-4" />
            <h2 className="font-headline text-3xl font-extrabold tracking-tight text-foreground mb-4">
              Ideas are the easy part. PostWriter writes the drafts.
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-8 leading-relaxed">
              PostWriter researches GitHub, Hacker News, and Reddit daily, then writes posts like
              these around your real experience — personalized to your role and tech stack.
            </p>
            <button
              onClick={redirectToGoogleLogin}
              className="bg-foreground text-background px-10 py-4 rounded-full font-headline font-bold text-sm uppercase tracking-widest shadow-lg hover:opacity-90 transition-all active:scale-95 inline-flex items-center gap-2"
            >
              Try Free for 7 Days
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-4 text-xs text-muted-foreground">No credit card required.</p>
          </div>

          <div>
            <h3 className="font-headline text-xs font-extrabold uppercase tracking-widest text-muted-foreground mb-4">
              Post ideas for other roles
            </h3>
            <div className="flex flex-wrap gap-2">
              {POST_IDEAS.filter((t) => t.slug !== topic.slug).map((t) => (
                <Link
                  key={t.slug}
                  to={`/post-ideas/${t.slug}`}
                  className="px-4 py-2 rounded-full bg-muted/50 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  {t.persona}
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>
    </>
  );
}
